import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Award, CheckCircle, Loader2, ArrowLeft, Play, DollarSign, 
  Users, Star, Building, Trophy
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '../lib/auth';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const VIDEO_URL = "https://storage.googleapis.com/msgsndr/hp00uSQfTXI2CWHYtxP7/media/696e9e0d8ec5c9c42bca22aa.mp4";

export default function AIBestyLinkPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    first_name: user?.name?.split(' ')[0] || '',
    last_name: user?.name?.split(' ').slice(1).join(' ') || '',
    email: user?.email || '',
    phone: user?.phone || '',
    consent: false
  });
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.first_name || !formData.last_name || !formData.email || !formData.phone) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (!formData.consent) {
      toast.error('Please agree to the terms to continue');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/ghl/ai-besty-signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          phone: formData.phone,
          tags: ['ai besty link'],
          custom_fields: {
            ai_besty_signup_date: new Date().toISOString(),
            ai_besty_consent: 'yes'
          }
        })
      });
      
      if (response.ok) {
        setSubmitted(true);
        toast.success('Welcome to the AI Besty Affiliate Program!');
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to submit. Please try again.');
      }
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };
  
  if (submitted) {
    return (
      <DashboardLayout>
        <div className="p-6 pt-20 max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-20"
          >
            <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-400" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-4">You're In!</h1>
            <p className="text-gray-400 mb-2 text-lg">Welcome to the AI Besty Affiliate Program</p>
            <p className="text-gray-500 mb-8">Your affiliate link will be sent to your email shortly.</p>
            
            <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 rounded-2xl p-6 border border-amber-500/20 mb-8 max-w-md mx-auto">
              <h3 className="text-amber-400 font-semibold mb-2">Your Commission</h3>
              <p className="text-3xl font-bold text-white mb-1">$60</p>
              <p className="text-sm text-gray-400">per qualified business that pays their $75 App Fee</p>
            </div>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/affiliate">
                <Button className="bg-cyan-500 hover:bg-cyan-600 text-black font-bold">
                  <Users className="w-4 h-4 mr-2" /> View Affiliate Dashboard
                </Button>
              </Link>
              <Link to="/dashboard">
                <Button variant="outline" className="border-white/20">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </DashboardLayout>
    );
  }
  
  return (
    <DashboardLayout>
      <div className="p-6 pt-20 max-w-4xl mx-auto" data-testid="ai-besty-link-page">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center mx-auto mb-4">
            <Award className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">AI Besty Affiliate Program</h1>
          <p className="text-gray-400">Earn $60 for every business you refer that qualifies</p>
        </motion.div>
        
        {/* Video Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <div className="relative rounded-2xl overflow-hidden bg-black aspect-video">
            <video 
              src={VIDEO_URL}
              controls
              className="w-full h-full object-contain"
              poster=""
            >
              Your browser does not support the video tag.
            </video>
          </div>
        </motion.div>
        
        {/* Benefits */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="grid sm:grid-cols-3 gap-4 mb-8"
        >
          <div className="bg-white/5 rounded-xl p-4 border border-white/10 text-center">
            <DollarSign className="w-8 h-8 text-green-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">$60</p>
            <p className="text-sm text-gray-400">Per Qualified Business</p>
          </div>
          <div className="bg-white/5 rounded-xl p-4 border border-white/10 text-center">
            <Building className="w-8 h-8 text-cyan-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">$120K</p>
            <p className="text-sm text-gray-400">Co-Op Grant for Businesses</p>
          </div>
          <div className="bg-white/5 rounded-xl p-4 border border-white/10 text-center">
            <Trophy className="w-8 h-8 text-amber-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">Best of Best</p>
            <p className="text-sm text-gray-400">Recognition Badge</p>
          </div>
        </motion.div>
        
        {/* Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-[#0d1525] rounded-2xl p-6 md:p-8 border border-white/10"
        >
          <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
            <Star className="w-5 h-5 text-amber-400" />
            Get Your Affiliate Link
          </h2>
          
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-400 mb-1 block">First Name *</Label>
                <Input
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  placeholder="John"
                  required
                  className="bg-white/5 border-white/10 text-white h-11"
                />
              </div>
              <div>
                <Label className="text-gray-400 mb-1 block">Last Name *</Label>
                <Input
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  placeholder="Doe"
                  required
                  className="bg-white/5 border-white/10 text-white h-11"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-gray-400 mb-1 block">Email *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="john@example.com"
                required
                className="bg-white/5 border-white/10 text-white h-11"
              />
            </div>
            
            <div>
              <Label className="text-gray-400 mb-1 block">Phone *</Label>
              <Input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="(555) 123-4567"
                required
                className="bg-white/5 border-white/10 text-white h-11"
              />
            </div>
            
            {/* Consent Checkbox */}
            <div className="bg-amber-500/10 rounded-xl p-4 border border-amber-500/20">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.consent}
                  onChange={(e) => setFormData({ ...formData, consent: e.target.checked })}
                  className="mt-1 w-5 h-5 rounded border-amber-500/50 bg-white/10 text-amber-500 focus:ring-amber-500"
                />
                <span className="text-sm text-gray-300 leading-relaxed">
                  Yes, send me my affiliate link to share with businesses who I believe should be Best of Best of their business category. I understand I will earn <span className="text-amber-400 font-semibold">$60</span> per business who qualifies for the <span className="text-amber-400 font-semibold">$120k Co-Op Grant</span> and pays their <span className="text-amber-400 font-semibold">$75 App Fee</span> for Final Approval.
                </span>
              </label>
            </div>
            
            <Button
              type="submit"
              disabled={submitting || !formData.consent}
              className="w-full h-12 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-bold text-lg disabled:opacity-50"
            >
              {submitting ? (
                <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Processing...</>
              ) : (
                <><Award className="w-5 h-5 mr-2" /> Get My Affiliate Link</>
              )}
            </Button>
          </form>
        </motion.div>
        
        {/* How It Works */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-8 p-6 bg-white/5 rounded-2xl border border-white/10"
        >
          <h3 className="text-lg font-bold text-white mb-4">How It Works</h3>
          <div className="grid sm:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center mx-auto mb-3">
                <span className="text-cyan-400 font-bold">1</span>
              </div>
              <h4 className="font-medium text-white mb-1">Get Your Link</h4>
              <p className="text-sm text-gray-400">Fill out the form above to receive your unique affiliate link</p>
            </div>
            <div className="text-center">
              <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-3">
                <span className="text-amber-400 font-bold">2</span>
              </div>
              <h4 className="font-medium text-white mb-1">Share & Refer</h4>
              <p className="text-sm text-gray-400">Share with businesses you believe deserve Best of Best recognition</p>
            </div>
            <div className="text-center">
              <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-3">
                <span className="text-green-400 font-bold">3</span>
              </div>
              <h4 className="font-medium text-white mb-1">Earn $60</h4>
              <p className="text-sm text-gray-400">Get paid when they qualify and pay their $75 App Fee</p>
            </div>
          </div>
        </motion.div>
      </div>
    </DashboardLayout>
  );
}
